import {
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useAudit } from "./auditContext";
import { FormattedMessage, useIntl } from "react-intl";
import ApiServices from "../../../service/apiService";
import { useEffect, useRef, useState } from "react";
import Account from "../../../utils/apiConfig";
import ErrorHandler from "../../../utils/errorConfig";
import ClearIcon from "@mui/icons-material/Clear";
import { Dialog } from "@progress/kendo-react-dialogs";
import { useNavigate } from "react-router-dom";
import { Alert } from "@mikros/shared-ui";
import AuthService from "../../../service/authService";
import { jwtDecode } from "jwt-decode";

export function SearchAuditFilter() {
  const {
    setAuditModel,
    setTotalUserCount,
    setSearchActive,
    formData,
    setFormData,
    setHasMoreData,
    auditNameSelected,
    setAuditNameSelected,
  } = useAudit();

  const accountInstance = Account.getInstance();
  const errorInstance = ErrorHandler.getErrorInstance();
  const nameRegex: RegExp = new RegExp(/^[a-zA-Z0-9\s.]+$/);
  const workRegex: RegExp = new RegExp(/^[a-zA-Z0-9\s.-]+$/);
  const maxUserNameLimit = 50;
  const maxCharLimit = 50;
  const intl = useIntl();
  const navigate = useNavigate();

  const [sub, setSub] = useState(null);
  const subRef = useRef(null);

  const [searchClicked, setSearchClicked] = useState<boolean>(false);

  const [auditList, setAuditList] = useState<any>("");
  const [auditorNameList, setAuditorNameList] = useState<any>("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [errorFlag, setErrorFlag] = useState<boolean>(false);

  console.log("print audit name selected", auditNameSelected);
  const validators = {
    auditNameValidator: (value: string) =>
      !value
        ? `${intl.formatMessage({
            id: "audit.searchAuditFilter.auditNameValidation",
          })}`
        : "",
    auditedByValidator: (value: string) =>
      value === "" || nameRegex.test(value)
        ? ""
        : `${intl.formatMessage({
            id: "audit.searchAuditFilter.auditedByValidation",
          })}`,

    deptRegexValidator: (value: string) =>
      !value
        ? ""
        : workRegex.test(value)
        ? ""
        : `${intl.formatMessage({
            id: "user.createUser.departmentValidation",
          })}`,
    desgRegexValidator: (value: string) =>
      !value
        ? ""
        : workRegex.test(value)
        ? ""
        : `${intl.formatMessage({
            id: "user.createUser.designationValidation",
          })}`,
    branchRegexValidator: (value: string) =>
      !value
        ? ""
        : workRegex.test(value)
        ? ""
        : `${intl.formatMessage({ id: "user.createUser.branchValidation" })}`,
    locationRegexValidator: (value: string) =>
      !value
        ? ""
        : workRegex.test(value)
        ? ""
        : `${intl.formatMessage({ id: "user.createUser.locationValidation" })}`,
  };

  console.log("auditor name list print", auditorNameList);

  const handleSearch = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    console.log("Inside handle search:", formData);

    // First validate audit name since it's required
    if (!formData.auditName) {
      setErrors({
        ...errors,
        auditName: intl.formatMessage({
          id: "audit.searchAuditFilter.auditNameValidation",
        }),
      });
      return;
    }

    // Validate all fields for invalid characters
    const validationErrors: Record<string, string> = {};

    if (formData.auditedBy && !nameRegex.test(formData.auditedBy)) {
      validationErrors.auditedBy = validators.auditedByValidator(
        formData.auditedBy
      );
    }
    if (formData.branchName && !workRegex.test(formData.branchName)) {
      validationErrors.branchName = validators.branchRegexValidator(
        formData.branchName
      );
    }
    if (formData.department && !workRegex.test(formData.department)) {
      validationErrors.department = validators.deptRegexValidator(
        formData.department
      );
    }
    if (formData.location && !workRegex.test(formData.location)) {
      validationErrors.location = validators.locationRegexValidator(
        formData.location
      );
    }
    if (formData.designation && !workRegex.test(formData.designation)) {
      validationErrors.designation = validators.desgRegexValidator(
        formData.designation
      );
    }

    // If there are any validation errors, set them and return
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Check if at least one field has data
    const hasValidInput = Object.values(formData).some(
      (value) => typeof value === "string" && value.trim() !== ""
    );

    if (!hasValidInput) {
      console.log("No search fields filled. Skipping API call.");
      return;
    }

    setSearchClicked(true);

    const filters = [
      "auditName",
      "auditedBy",
      "branchName",
      "department",
      "location",
      "designation",
    ];
    const queryParams = filters
      .filter((key) => formData[key]?.trim())
      .map((key) => `${key}=${encodeURIComponent(formData[key].trim())}`)
      .join("&");

    console.log("Query String:", queryParams);

    const api = ApiServices.getInstance();
    if (accountInstance.accntId && formData.auditName) {
      api
        .makeRequest(
          `${accountInstance.accntId}/useraudit/filter?${queryParams}`,
          "GET"
        )
        .then(async (data) => {
          if (data && data.errorMessage) {
            console.log("I entered error code handling in users");
            setErrorFlag(true);
            await errorInstance.updateErrorCode(data.errorMessage);
            return;
          } else {
            setAuditModel(data);
            setTotalUserCount(data?.audits.length);
            setSearchActive(true);
            const hasDataLeft = data?.lastKey !== null;
            setHasMoreData(hasDataLeft);
          }
        });
    }
  };

  const handleClear = (field: string) => {
    setFormData({
      ...formData,
      [field]: "",
    });

    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAuditNameChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setAuditNameSelected(value);
    // Clear audit name error when value is selected
    if (value) {
      setErrors((prev) => ({ ...prev, auditName: "" }));
    }
  };

  const handleAuditedByChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear audited by error when value is selected
    if (value) {
      setErrors((prev) => ({ ...prev, auditedBy: "" }));
    }
  };

  const handleChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    let errorMsg = "";
    if (name === "auditedBy") {
      errorMsg = validators.auditedByValidator(value);
    } else if (name === "branchName") {
      errorMsg = validators.branchRegexValidator(value);
    } else if (name === "department") {
      errorMsg = validators.deptRegexValidator(value);
    } else if (name === "location") {
      errorMsg = validators.locationRegexValidator(value);
    } else if (name === "designation") {
      errorMsg = validators.desgRegexValidator(value);
    }

    setErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const handleClearAll = () => {
    setFormData({
      designation: "",
      auditName: "",
      auditedBy: "",
      branchName: "",
      location: "",
      department: "",
    });
    setErrors({});
    setAuditNameSelected("");

    if (searchClicked) {
      let api = ApiServices.getInstance();
      if (accountInstance.accntId) {
        api
          .makeRequest(`${accountInstance.accntId}/useraudit?limit=20`, "GET")
          .then(async (data) => {
            if (data && data.errorMessage) {
              console.log("I entered error code handling in users");
              setErrorFlag(true);
              await errorInstance.updateErrorCode(data.errorMessage);
              return;
            } else {
              setAuditModel(data);
              setTotalUserCount(data.audits.length);
              setSearchActive(false);
              setHasMoreData(true);
            }
          });
      }
      setSearchClicked(false);
    }
  };

  useEffect(() => {
    async function fetchSub() {
      let a = await AuthService.getInstance().getIdToken();
      console.log("id token course", a);
      const decoded: any = jwtDecode(a);
      const subValue = decoded["sub"];
      setSub(subValue);
      subRef.current = subValue;
      console.log("print tenant id inside use effect", subValue);
    }
    fetchSub();
  }, []);

  useEffect(() => {
    if (subRef.current !== null) {
      let api = ApiServices.getInstance();
      if (accountInstance.accntId) {
        api
          .makeRequest(
            `${accountInstance.accntId}/audit/auditnames?userId=${subRef.current}`,
            "GET"
          )
          .then(async (data) => {
            if (data && data.errorMessage) {
              setErrorFlag(true);
              await errorInstance.updateErrorCode(data.errorMessage);
            } else {
              setAuditList(data);
            }
          });
      }
    }
  }, [subRef.current]);

  useEffect(() => {
    let api = ApiServices.getInstance();
    if (accountInstance.accntId) {
      api
        .makeRequest(
          `${accountInstance.accntId}/customersadmin/auditors`,
          "GET"
        )
        .then(async (data) => {
          if (data && data.errorMessage) {
            setErrorFlag(true);
            await errorInstance.updateErrorCode(data.errorMessage);
          } else {
            // setAuditorNameList(data)
            const fullNames = data.map(
              (user: { fname: any; lname: any }) =>
                `${user.fname} ${user.lname}`
            );
            console.log("full names print", fullNames);
            setAuditorNameList(fullNames);
          }
        });
    }
  }, []);

  return (
    <div>
      <Card sx={{ padding: 1, boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <form onSubmit={handleSearch}>
            {[
              [
                {
                  id: "audit.searchAuditFilter.auditName",
                  name: "auditName",
                  value: formData.auditName,
                  error: errors.auditName,
                  isDropdown: true,
                },
                {
                  id: "audit.searchAuditFilter.auditedBy",
                  name: "auditedBy",
                  value: formData.auditedBy,
                  error: errors.auditedBy,
                  isDropdown: true,
                },
                {
                  id: "analytics.userView.branch",
                  name: "branchName",
                  value: formData.branchName,
                  error: errors.branchName,
                  maxLength: maxCharLimit,
                },
              ],
              [
                {
                  id: "analytics.userView.dept",
                  name: "department",
                  value: formData.department,
                  error: errors.department,
                  maxLength: maxCharLimit,
                },
                {
                  id: "analytics.userView.location",
                  name: "location",
                  value: formData.location,
                  error: errors.location,
                  maxLength: maxCharLimit,
                },
                {
                  id: "analytics.userView.desg",
                  name: "designation",
                  value: formData.designation,
                  error: errors.designation,
                  maxLength: maxCharLimit,
                },
              ],
            ].map((row, rowIndex) => (
              <div key={rowIndex} className="grid grid-cols-3 gap-4">
                {row.map((field) => (
                  <FormControl
                    key={field.name}
                    margin="dense"
                    sx={{ width: "16rem" }}
                  >
                    <Typography variant="subtitle1">
                      <FormattedMessage id={field.id} />
                    </Typography>
                    {field.isDropdown ? (
                      <Select
                        value={field.value}
                        onChange={
                          field.name === "auditName"
                            ? handleAuditNameChange
                            : handleAuditedByChange
                        }
                        name={field.name}
                        displayEmpty
                        error={!!field.error}
                        renderValue={(selected) =>
                          selected
                            ? selected
                            : intl.formatMessage({
                                id:
                                  field.name === "auditName"
                                    ? "audit.selectAuditName"
                                    : "audit.selectAuditedBy",
                              })
                        }
                        sx={{
                          backgroundColor: "white",
                        }}
                      >
                        {/* {Array.isArray(
                          field.name === "auditName"
                            ? auditList?.result
                            : auditorNameList
                        ) &&
                          (field.name === "auditName"
                            ? auditList?.result
                            : auditorNameList
                          )?.map((item: any) => (
                            <MenuItem
                              key={item?.auditName || item}
                              value={item?.auditName || item}
                            >
                              {item?.auditName || item}
                            </MenuItem>
                          ))} */}

                        {Array.isArray(
                          field.name === "auditName"
                            ? auditList?.result
                            : auditorNameList
                        ) &&
                          (field.name === "auditName"
                            ? auditList?.result
                            : auditorNameList
                          )?.map((item: any) => {
                            const value =
                              typeof item === "object" ? item.auditName : item;

                            return (
                              <MenuItem key={value} value={value}>
                                {value}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    ) : (
                      <TextField
                        error={!!field.error}
                        helperText={field.error}
                        InputLabelProps={{ shrink: false }}
                        name={field.name}
                        value={field.value}
                        onChange={handleChange}
                        inputProps={{ maxLength: field.maxLength }}
                        InputProps={{
                          disableUnderline: true,
                          style: { backgroundColor: "white" },
                          endAdornment: (
                            <InputAdornment position="end">
                              {field.value && (
                                <IconButton
                                  onClick={() => handleClear(field.name)}
                                  edge="end"
                                  size="small"
                                  sx={{
                                    backgroundColor: "transparent",
                                    "&:hover": {
                                      backgroundColor: "#E1E2E9",
                                    },
                                  }}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    {field.isDropdown && field.error && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1 }}
                      >
                        {field.error}
                      </Typography>
                    )}
                  </FormControl>
                ))}
              </div>
            ))}

            <div className="flex justify-end items-end space-x-2 mt-4">
              <Button
                type="submit"
                variant="contained"
                disableRipple
                sx={{
                  borderRadius: "8px",
                  textTransform: "capitalize",
                  color: "white",
                  backgroundColor: "#6464E5",
                  "&:hover": {
                    backgroundColor: "#E1E2E9",
                    color: "black",
                  },
                  display: "inline-block",
                }}
              >
                <FormattedMessage id="analytics.userView.search" />
              </Button>

              <Button
                type="button"
                variant="contained"
                disableRipple
                sx={{
                  borderRadius: "8px",
                  textTransform: "capitalize",
                  color: "black",
                  backgroundColor: "#E1E2E9",
                  "&:hover": {
                    backgroundColor: "#E1E2E9",
                  },
                  display: "inline-block",
                }}
                onClick={() => {
                  handleClearAll();
                }}
              >
                <FormattedMessage id="analytics.userView.clear" />
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      {errorFlag && (
        <Dialog className="errorDialogue">
          <Alert
            description={intl.formatMessage({
              id: errorInstance.errorCode,
            })}
            buttonText={`${intl.formatMessage({
              id: "users.buttonText.Ok",
            })}`}
            icon={"errorShape.png"}
            iconMsg={intl.formatMessage({
              id: "iconMsgError",
            })}
            backgroundColor={"mk-red"}
            onClick={() => {
              setErrorFlag(false);
              if (errorInstance.permDenied === true) {
                navigate("/home");
              } else {
                navigate("/audit");
              }
            }}
          />
        </Dialog>
      )}
    </div>
  );
}
